// Orquestador de "vuelta" del TPV (móvil bloqueado, pestaña en segundo
// plano, red caída, navegador congelado/restaurado desde bfcache, hub
// reconectado o API reiniciada).
//
// Todos los disparadores entran por `trigger(reason)`; se agrupan con un
// debounce corto y se ejecuta UNA resincronización a la vez. Si llega otro
// disparador mientras corre, se encola UNA sola repetición (con la unión
// de motivos) que corre al terminar: nunca se pierde una vuelta ni se
// solapan dos.
//
// Puro (sin window/document) para poder probarlo con temporizadores falsos;
// `attachResumeListeners` conecta los eventos del navegador.

export const RESUME_DEBOUNCE_MS = 250;
/** Si el reloj salta más que esto entre ticks, la página estuvo congelada. */
export const FREEZE_THRESHOLD_MS = 15_000;
export const FREEZE_TICK_MS = 5_000;

const unionReasons = (a, b) => Array.from(new Set([...(a || []), ...(b || [])]));

/**
 * @param {object} opts
 * @param {(ctx: {reasons: string[], awayMs: number}) => Promise<any>} opts.run
 * @param {number} [opts.debounceMs]
 * @param {Function} [opts.setTimer]
 * @param {Function} [opts.clearTimer]
 */
export function createResumeOrchestrator({
  run,
  debounceMs = RESUME_DEBOUNCE_MS,
  setTimer = (fn, ms) => setTimeout(fn, ms),
  clearTimer = (t) => clearTimeout(t),
} = {}) {
  let timer = null;
  let running = null; // promesa en curso
  let queued = null; // { reasons, awayMs } para la repetición
  let collecting = null; // { reasons, awayMs } durante el debounce
  let disposed = false;
  let runs = 0;

  const merge = (acc, reason, awayMs) => ({
    reasons: unionReasons(acc?.reasons, [reason]),
    awayMs: Math.max(acc?.awayMs || 0, awayMs || 0),
  });

  const execute = (ctx) => {
    runs += 1;
    running = Promise.resolve()
      .then(() => run(ctx))
      .catch(() => {})
      .finally(() => {
        running = null;
        if (disposed) return;
        if (queued) {
          const next = queued;
          queued = null;
          execute(next);
        }
      });
    return running;
  };

  const fire = () => {
    timer = null;
    const ctx = collecting;
    collecting = null;
    if (!ctx || disposed) return;
    if (running) {
      queued = queued ? { reasons: unionReasons(queued.reasons, ctx.reasons), awayMs: Math.max(queued.awayMs, ctx.awayMs) } : ctx;
      return;
    }
    execute(ctx);
  };

  return {
    /** Pide una resincronización. `awayMs` = cuánto tiempo estuvo fuera (si se sabe). */
    trigger(reason, { awayMs = 0, immediate = false } = {}) {
      if (disposed) return;
      collecting = merge(collecting, reason, awayMs);
      if (timer) clearTimer(timer);
      if (immediate) fire();
      else timer = setTimer(fire, debounceMs);
    },
    /** Promesa de la resincronización en curso (o resuelta si no hay). */
    whenIdle() {
      return running || Promise.resolve();
    },
    isRunning: () => !!running,
    hasQueued: () => !!queued,
    runCount: () => runs,
    dispose() {
      disposed = true;
      if (timer) clearTimer(timer);
      timer = null;
      queued = null;
      collecting = null;
    },
  };
}

/**
 * Detector de congelación: el navegador puede parar los timers (pestaña
 * congelada, pantalla apagada) sin emitir `visibilitychange`. Si entre dos
 * ticks pasa mucho más tiempo del esperado, avisamos con lo que estuvo fuera.
 */
export function createFreezeDetector({
  onFreeze,
  isHidden = () => false,
  tickMs = FREEZE_TICK_MS,
  thresholdMs = FREEZE_THRESHOLD_MS,
  now = () => Date.now(),
  setIntervalFn = (fn, ms) => setInterval(fn, ms),
  clearIntervalFn = (t) => clearInterval(t),
} = {}) {
  let last = now();
  const handle = setIntervalFn(() => {
    const t = now();
    const gap = t - last;
    last = t;
    // 2026-10-27 · Pestaña oculta: Chrome espacia los temporizadores (hasta 1
    // por minuto) y eso no es una congelación. Al volver a verse ya dispara
    // «visible» con el tiempo fuera; aquí no se hace nada.
    if (isHidden()) return;
    if (gap - tickMs > thresholdMs) onFreeze?.(gap);
  }, tickMs);
  return {
    /** Para resetear tras una vuelta ya gestionada por otro evento. */
    touch() {
      last = now();
    },
    stop() {
      clearIntervalFn(handle);
    },
  };
}

/**
 * Conecta los eventos del navegador al orquestador. Devuelve `detach()`.
 * `win`/`doc` inyectables para los tests.
 */
export function attachResumeListeners(orchestrator, {
  win = typeof window !== "undefined" ? window : null,
  doc = typeof document !== "undefined" ? document : null,
  now = () => Date.now(),
  freeze = {},
} = {}) {
  if (!win || !doc) return () => {};
  let hiddenAt = doc.visibilityState === "hidden" ? now() : null;

  const awayFromHidden = () => (hiddenAt != null ? Math.max(0, now() - hiddenAt) : 0);

  const onVisibility = () => {
    if (doc.visibilityState === "hidden") {
      if (hiddenAt == null) hiddenAt = now();
      return;
    }
    const awayMs = awayFromHidden();
    hiddenAt = null;
    detector.touch();
    orchestrator.trigger("visible", { awayMs });
  };
  const onPageShow = (e) => {
    // bfcache: la página vuelve "tal cual" de hace minutos/horas.
    if (!e?.persisted) return;
    const awayMs = awayFromHidden();
    hiddenAt = null;
    detector.touch();
    orchestrator.trigger("pageshow", { awayMs: Math.max(awayMs, FREEZE_THRESHOLD_MS) });
  };
  const onPageHide = () => {
    if (hiddenAt == null) hiddenAt = now();
  };
  const onFocus = () => {
    if (doc.visibilityState === "hidden") return;
    orchestrator.trigger("focus", { awayMs: awayFromHidden() });
  };
  const onOnline = () => orchestrator.trigger("online", { awayMs: awayFromHidden() });
  // Chrome: la pestaña congelada por el sistema vuelve con `resume`.
  const onResume = () => {
    detector.touch();
    orchestrator.trigger("resume", { awayMs: Math.max(awayFromHidden(), FREEZE_THRESHOLD_MS) });
  };

  const detector = createFreezeDetector({
    isHidden: () => doc.visibilityState === "hidden",
    ...freeze,
    now: freeze.now || now,
    onFreeze: (gap) => orchestrator.trigger("freeze", { awayMs: gap }),
  });

  doc.addEventListener("visibilitychange", onVisibility);
  doc.addEventListener("resume", onResume);
  win.addEventListener("pageshow", onPageShow);
  win.addEventListener("pagehide", onPageHide);
  win.addEventListener("focus", onFocus);
  win.addEventListener("online", onOnline);

  return () => {
    detector.stop();
    doc.removeEventListener("visibilitychange", onVisibility);
    doc.removeEventListener("resume", onResume);
    win.removeEventListener("pageshow", onPageShow);
    win.removeEventListener("pagehide", onPageHide);
    win.removeEventListener("focus", onFocus);
    win.removeEventListener("online", onOnline);
  };
}
